import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateID } from "@/lib/constants";
import { StatusBadge, ServiceBadge } from "@/components/Badges";
import { Trash, ArrowUUpLeft, WarningOctagon, TrashSimple } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function TrashPage() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [permConfirm, setPermConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reservations/trash");
      setItems(data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSel = items.length > 0 && items.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(() => allSel ? new Set() : new Set(items.map((r) => r.id)));
  const selectedList = items.filter((r) => selected.has(r.id));
  const selectedPaid = selectedList.filter((r) => r.has_payment).length;

  const restore = async () => {
    setBusy(true);
    try {
      const ids = Array.from(selected);
      await api.post("/reservations/bulk-restore", { ids });
      toast.success(`${ids.length} reservasi dipulihkan`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const permanentDelete = async () => {
    setBusy(true);
    try {
      const ids = Array.from(selected);
      const { data } = await api.post("/reservations/bulk-permanent-delete", { ids });
      if (data.blocked?.length) toast.warning(`${data.deleted.length} dihapus permanen. ${data.blocked.length} dilindungi (punya data pembayaran).`);
      else toast.success(`${data.deleted.length} reservasi dihapus permanen`);
      setPermConfirm(false);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="animate-fade-up space-y-5" data-testid="trash-page">
      <div className="flex items-center gap-2">
        <TrashSimple size={26} weight="duotone" className="text-rose-500" />
        <h1 className="font-heading text-3xl font-extrabold text-slate-800">Sampah</h1>
        <span className="ml-2 rounded-full bg-rose-50 px-3 py-1 text-sm font-bold text-rose-600" data-testid="trash-count">{items.length}</span>
      </div>
      <p className="text-sm text-slate-500">Reservasi yang dipindahkan ke Sampah. Pulihkan kapan saja. Reservasi dengan data pembayaran dilindungi dari penghapusan permanen.</p>

      {selected.size > 0 && (
        <div data-testid="trash-action-bar" className="sticky top-2 z-10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#7C4DE0]/30 bg-[#F3ECFF] px-4 py-3 shadow-sm">
          <span className="text-sm font-bold text-[#6A38C7]">{selected.size} dipilih</span>
          <div className="flex items-center gap-2">
            <button data-testid="trash-restore" onClick={restore} disabled={busy} className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-60"><ArrowUUpLeft size={16} weight="bold" /> Pulihkan</button>
            {isSuper && <button data-testid="trash-permanent" onClick={() => setPermConfirm(true)} disabled={busy} className="inline-flex items-center gap-1.5 rounded-xl bg-rose-700 px-4 py-2 text-sm font-bold text-white hover:bg-rose-800 disabled:opacity-60"><Trash size={16} weight="bold" /> Hapus Permanen</button>}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-4 border-rose-200 border-t-rose-500" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-rose-200 bg-white/50 p-12 text-center text-sm text-slate-500">Sampah kosong.</div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-rose-100/70 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-rose-100 bg-[#F7F0FF] text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                <th className="px-3 py-3"><input data-testid="trash-select-all" type="checkbox" checked={allSel} onChange={toggleAll} className="h-4 w-4 cursor-pointer accent-[#7C4DE0]" /></th>
                <th className="px-3 py-3">Tgl / Jam</th>
                <th className="px-3 py-3">Nama</th>
                <th className="px-3 py-3">Layanan</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3">Dihapus oleh</th>
                <th className="px-3 py-3">Pembayaran</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} data-testid={`trash-row-${r.id}`} className={`border-b border-rose-50 last:border-0 ${selected.has(r.id) ? "bg-[#F3ECFF]" : ""}`}>
                  <td className="px-3 py-3"><input data-testid={`trash-select-${r.id}`} type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} className="h-4 w-4 cursor-pointer accent-[#7C4DE0]" /></td>
                  <td className="px-3 py-3"><div className="font-semibold text-slate-700">{formatDateID(r.tanggal_kunjungan)}</div><div className="text-xs text-slate-400">{r.jam_kunjungan}</div></td>
                  <td className="px-3 py-3 font-semibold text-slate-800">{r.nama_lengkap}</td>
                  <td className="px-3 py-3"><ServiceBadge service={r.jenis_layanan} /></td>
                  <td className="px-3 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-3 py-3 text-xs text-slate-500">{r.deleted_by_name || r.deleted_by || "-"}</td>
                  <td className="px-3 py-3">{r.has_payment ? <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-bold text-amber-700">Ada</span> : <span className="text-xs text-slate-400">-</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={permConfirm} onOpenChange={(v) => !v && setPermConfirm(false)}>
        <AlertDialogContent className="rounded-2xl" data-testid="perm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><WarningOctagon size={20} weight="fill" className="text-rose-600" /> Hapus permanen {selected.size} reservasi?</AlertDialogTitle>
            <AlertDialogDescription>
              Tindakan ini <b>tidak dapat dibatalkan</b>. Reservasi dengan data pembayaran akan otomatis dilindungi dan tidak ikut terhapus.
              {selectedPaid > 0 && <span className="mt-2 block rounded-lg bg-amber-50 px-3 py-2 font-semibold text-amber-700">⚠️ {selectedPaid} reservasi berbayar akan dilewati (dilindungi).</span>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-perm-delete" disabled={busy} onClick={permanentDelete} className="bg-rose-700 hover:bg-rose-800">Hapus Permanen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
